import { ELandingType } from '../../../types/landingType';
import { JoinOtaLoadable } from '../../joinOtaIndividual/JoinOta/JoinOtaLoadable';
import { JoinOtaLegalShortLoadable } from '../../joinOtaLegalShort/JoinOtaLegalShort/JoinOtaLegalShortLoadable';
import { ActivateInsuranceLoadable } from '../../activateInsurance/ActivateInsurance/ActivateInsuranceLoadable';
import { MegalandingRootLoadable } from '../../megalanding/MegalandingRoot/MegalandingRootLoadable';
import { ErrorLoadable } from '../../joinOtaIndividual/Error/ErrorLoadable';

import { useSelector } from 'react-redux';

import { selectLandingType } from '../../../selectors/landingType/selectLandingType';
import { LoadableComponent } from '@loadable/component';
import { LoadableErrorBoundary } from '../../../../packages/LoadableHelpers';
import React, { useEffect } from 'react';
import { useApplicationContext } from '../../../utils/applicationContext';
import { useAppDispatch, useHeaderHeight, useMakeHeaderSticky } from '../../../hooks';
import { setLandingType } from '../../../slices/landingType';
import { useLandingsRouting } from '../../../hooks/useLandingsRouting';
import { TStep } from '../../../types/step';
import { isStepValidForLandingType } from '../../../utils/steps';
import { BusinessEntityFlowRootLoadable } from '../../businessEntityFlow/BusinessEntityFlowRoot/BusinessEntityFlowRootLoadable';
import { SurchargePerGuestLoadable } from '../../surchargePerGuest/SurchargePerGuest/SurchargePerGuestLoadable';
import { UpdateMobileAppLoadable } from '../../updateMobileApp/UpdateMobileApp/UpdateMobileAppLoadable';
import { OnlineBookingProfileLoadable } from '../../onlineBookingProfile/OnlineBookingProfile/OnlineBookingProfileLoadable';

export const Root = () => {
  const { logger } = useApplicationContext();
  const dispatch = useAppDispatch();
  let Component: LoadableComponent<unknown>;
  const landingType = useSelector(selectLandingType);
  const { routeToStep } = useLandingsRouting();

  useHeaderHeight();
  useMakeHeaderSticky();

  switch (landingType) {
    case ELandingType.JoinOtaCian:
      Component = JoinOtaLoadable;
      break;
    case ELandingType.JoinOtaLegalShort:
      Component = JoinOtaLegalShortLoadable;
      break;
    case ELandingType.ActivateInsurance:
      Component = ActivateInsuranceLoadable;
      break;
    case ELandingType.Megalanding:
      Component = MegalandingRootLoadable;
      break;
    case ELandingType.BusinessEntityFlow:
      Component = BusinessEntityFlowRootLoadable;
      break;
    case ELandingType.SurchargePerGuest:
      Component = SurchargePerGuestLoadable;
      break;
    case ELandingType.UpdateMobileApp:
      Component = UpdateMobileAppLoadable;
      break;
    case ELandingType.OnlineBookingProfile:
      Component = OnlineBookingProfileLoadable;
      break;
    default:
      Component = ErrorLoadable;
  }

  const handleRetryLoadContent = React.useCallback(() => {
    Component.load();
  }, [Component]);

  useEffect(() => {
    // Перезаписываем историю на случай, если:
    // случилась ошибка –> выполнился редирект –> юзер вернулся назад.
    // Если не почистить, следующий popstate произойдет с пейлоадом ошибки
    window.history.replaceState({}, '');
  }, []);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const historyStep = event.state?.step ?? 'megalanding';

      if (historyStep === 'megalanding') {
        dispatch(setLandingType({ landingType: ELandingType.Megalanding }));

        return;
      }

      const [landingType, step] = historyStep.split('.');

      // Проверяем, что step является допустимым значением
      const isValidStep = isStepValidForLandingType(landingType as ELandingType, step);

      if (!isValidStep) {
        window.location.reload();
      }

      routeToStep(landingType as ELandingType, step as TStep);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [routeToStep]);

  return (
    <LoadableErrorBoundary logger={logger} onRetry={handleRetryLoadContent}>
      <Component />
    </LoadableErrorBoundary>
  );
};
